import { lessonSchema, type Lesson } from '@fluent/types';
import { createLazyContent, type LazyContentStatus } from './lazyContent.js';

/**
 * Bundled content loader — one lesson index, filled in two ways.
 *
 * MAIN COURSE (every course under `content/courses/` except the phrases
 * course): every `lesson-*.json` is imported eagerly at build time via
 * Vite's glob, validated with the Zod schema and indexed by lesson id when
 * this module loads. `getLesson` answers synchronously; the lesson engine
 * works offline (the PWA precaches the `course-content` chunk).
 *
 * PHRASES COURSE (`content/courses/ka-en-phrases/`, ~1 MB of JSON): LAZY.
 * Most learners never open ფრაზები, so its lessons are not part of the
 * app's start-up download. They live in their own `phrases-content` chunk
 * (vite.config.ts manualChunks) that is fetched the first time a screen
 * asks for it — `loadPhrasesLessons()`, usually through the
 * `usePhrasesContent()` hook. The service worker still precaches that chunk
 * in the background, so phrases work offline once the app is installed.
 * Only the phrases LESSONS are lazy: its course / unit / skill JSON (a few
 * KB) stays eager in course.ts, so ids, order and the free tier are known
 * without the lessons.
 *
 * Until the phrases lessons are loaded, `getLesson(phraseId)` returns null
 * and `listLessonIds()` lists the main course only.
 */

const mainLessonModules = import.meta.glob<unknown>(
  ['../../../../content/courses/**/lesson-*.json', '!../../../../content/courses/ka-en-phrases/**'],
  { eager: true, import: 'default' },
);

const phrasesLessonModules = import.meta.glob<unknown>(
  '../../../../content/courses/ka-en-phrases/**/lesson-*.json',
  { import: 'default' },
);

/**
 * Validate a batch of lesson modules and add them to `index` — all or
 * nothing. Throws on invalid JSON or on an id that is already in the index
 * (e.g. a phrases lesson reusing a main-course id) or twice in the batch;
 * the index is untouched then.
 */
export function indexLessons(
  index: Map<string, Lesson>,
  entries: ReadonlyArray<readonly [path: string, raw: unknown]>,
): void {
  const batch = new Map<string, Lesson>();
  for (const [path, raw] of entries) {
    const parsed = lessonSchema.safeParse(raw);
    if (!parsed.success) {
      throw new Error(`Invalid lesson JSON at ${path}: ${parsed.error.message}`);
    }
    const lesson = parsed.data;
    if (index.has(lesson.id) || batch.has(lesson.id)) {
      throw new Error(`Duplicate lesson id "${lesson.id}" (at ${path})`);
    }
    batch.set(lesson.id, lesson);
  }
  for (const [id, lesson] of batch) index.set(id, lesson);
}

const lessonsById = new Map<string, Lesson>();
indexLessons(lessonsById, Object.entries(mainLessonModules));

const phrasesLessons = createLazyContent(phrasesLessonModules, (entries) =>
  indexLessons(lessonsById, entries),
);

/** Returns the lesson for an id, or null if unknown — or if it is a phrases
 *  lesson and the phrases lessons are not loaded yet. */
export function getLesson(lessonId: string): Lesson | null {
  return lessonsById.get(lessonId) ?? null;
}

/** Every lesson id in the index (stable, unordered): the main course always,
 *  the phrases course only once `loadPhrasesLessons()` has finished. Useful
 *  for tests and dev tools. */
export function listLessonIds(): string[] {
  return [...lessonsById.keys()];
}

/** Fetch + validate + index the phrases course's lessons. Idempotent: every
 *  caller shares one in-flight load, and once loaded it resolves at once.
 *  Rejects on failure (offline, invalid content); calling it again retries. */
export function loadPhrasesLessons(): Promise<void> {
  return phrasesLessons.load();
}

/** True once the phrases lessons are in the index (`getLesson` finds them). */
export function arePhrasesLessonsLoaded(): boolean {
  return phrasesLessons.status() === 'ready';
}

export type PhrasesLessonsStatus = LazyContentStatus;

/** 'idle' (never asked for) | 'loading' | 'ready' | 'error'. */
export function getPhrasesLessonsStatus(): PhrasesLessonsStatus {
  return phrasesLessons.status();
}

/** Hear every status change of the phrases lessons (the React bridge —
 *  `usePhrasesContent`). Returns the unsubscribe function. */
export function subscribePhrasesLessons(listener: () => void): () => void {
  return phrasesLessons.subscribe(listener);
}
